import { yupResolver } from '@hookform/resolvers/yup';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  PartialPickPayload,
  PickRecordPayload,
} from 'common/api/dto/harvestDataApi.types';
import { HaulRateDto, PickRateDto } from 'common/api/dto/rates.dto';
import {
  useGetContractorsQuery,
  useGetHaulRatesQuery,
  useGetOtherRatesQuery,
} from 'common/api/endpoints/harvestData';
import { handleError } from 'common/api/utils/handleError';
import { validateCoordinateInfo } from 'common/components/LocationButton/LocationButton';
import { WizardNumericInput } from 'common/components/WizardControls/WizardNumericInput';
import { ScheduledPick, pickTypeLabels } from 'common/models';
import { DateOnlyFormat } from 'common/models/dateType';
import { Contractor } from 'common/models/harvestData/contractor';
import { roundBins } from 'common/models/harvestData/utilities';
import {
  OtherRateType,
  mapOtherRates,
} from 'common/models/settings/otherRates';
import * as notifier from 'common/services/notification';
import { darkNavy, orange, white } from 'common/styles/colors';
import usePickScheduleActions from 'features/pick-schedule-views/hooks/usePickScheduleActions';
import { formatPickDetailDate } from 'features/pick-schedule-views/utils/pickDetailUtils';
import {
  createDefaultHarvestRecords,
  createDefaultHaulRecords,
  createHarvestRecords,
  createHaulRecords,
  formatFromTimeDuration,
} from 'features/pick-schedule-views/utils/pickRecordHelpers';
import moment from 'moment';
import { FC, createContext, useEffect, useMemo, useState } from 'react';
import {
  FieldError,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Constants } from 'utils/constants';
import { pickDataSchema } from 'utils/schemas/pickDataSchema';
import { BlueWarning, DataEntryRow } from '../SchedulePickWizard/styles';
import { ItemContainer } from '../WizardComponents/ItemContainer';
import { PickDataWizardContainer } from '../WizardComponents/PickDataWizardContainer';
import { PickSummary } from '../WizardComponents/PickSummary';
import { WizardHeader } from '../WizardComponents/WizardHeader';
import {
  ButtonSection,
  CancelBtn,
  InfoLabel,
  Textarea,
  WizardDivider,
  WizardRow,
} from '../WizardComponents/wizardStyles';
import { HarvestData, HarvestDataForm } from './HarvestDataForm';
import { HaulData, HaulDataForm } from './HaulDataForm';
import {
  DataInfoValue,
  MobileWrapper,
  NavyButton,
  NavyNotes,
  NavyPrompt,
  SummaryContainer,
  TotalBinsContainer,
} from './styles';

export type PickDataValues = {
  harvestDataRecords: HarvestData[];
  haulDataRecords: HaulData[];
  binsLeft: string | null;
  fbPerTree: string | null;
  notes: string | null;
};

const AssignmentLabel = styled(InfoLabel)`
  min-width: 80px; // Prevent excessive wrapping.
`;

export const HaulRateWarning = (
  <BlueWarning>
    <ErrorOutlineIcon />
    <span>
      Warning: this pick was added with a mileage rate that is no longer active.
      Updating the mileage will update the pick to the current rate and cannot
      be reverted.
    </span>
  </BlueWarning>
);

export const PickDataWizardContext = createContext<{
  contractors?: Contractor[];
  haulers?: Contractor[];
  haulRates?: HaulRateDto[];
  isLoading: boolean;
  binsEntered: boolean;
  setTotalBinsPicked: React.Dispatch<React.SetStateAction<number>>;
}>(null!);

export const PickDataWizard: FC<{
  scheduledPick: ScheduledPick;
  pickRates?: PickRateDto[];
}> = ({ scheduledPick, pickRates }) => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { savePartialPickData, savePickData } = usePickScheduleActions();
  const {
    schedule,
    block,
    blockId,
    coordinator,
    packHouse,
    market,
    bins,
    cleanPick,
    pickType,
    pickAssignment,
    partialPickRecords,
    pickRecord,
    pool,
    size,
    notes,
  } = scheduledPick;

  const {
    data: contractors,
    isLoading,
    error: fetchCrewsError,
  } = useGetContractorsQuery({ activeOnly: true });
  const {
    data: haulRates,
    isLoading: isLoadingRates,
    error: fetchRatesError,
  } = useGetHaulRatesQuery({
    pickDate: moment(schedule.pickDay).format(DateOnlyFormat),
  });
  const { data: otherRates, error: otherRatesError } = useGetOtherRatesQuery();
  const [totalBinsPicked, setTotalBinsPicked] = useState(0);
  const binsEntered = useMemo(
    () => totalBinsPicked > 0 || !!pickRecord,
    [totalBinsPicked, pickRecord],
  );

  const otherRatesMap = useMemo(
    () => mapOtherRates(otherRates || []),
    [otherRates],
  );

  const defaultPickRate: PickRateDto | undefined = useMemo(
    () => pickRates?.find(dto => dto.varietyId === block.variety?.id),
    [pickRates, block.variety],
  );

  const harvestDataRecords: HarvestData[] | Omit<HarvestData, 'id'>[] = useMemo(
    () =>
      pickRecord?.contractorRecords?.length > 0
        ? createHarvestRecords(pickRecord.contractorRecords)
        : createDefaultHarvestRecords(
            pickAssignment.contractorPickAssignment,
            partialPickRecords,
            defaultPickRate?.rate.toString(),
          ),
    [pickRecord, pickAssignment, partialPickRecords, defaultPickRate],
  );

  const haulDataRecords: HaulData[] | Omit<HaulData, 'id'>[] = useMemo(
    () =>
      pickRecord?.contractorRecords?.length > 0
        ? createHaulRecords(pickRecord.haulerRecords)
        : createDefaultHaulRecords(pickAssignment.haulers),
    [pickAssignment, pickRecord],
  );

  const methods = useForm<PickDataValues>({
    defaultValues: {
      harvestDataRecords,
      haulDataRecords,
      binsLeft: pickRecord?.binsLeft.toString() || null,
      fbPerTree: pickRecord?.fbPerTree.toString() || null,
      notes: pickRecord?.notes || null,
    },
    resolver: yupResolver(pickDataSchema),
    mode: 'all',
    context: {
      pickRateMin: otherRatesMap.get(OtherRateType.PickRateMin)?.rate,
      pickRateMax: otherRatesMap.get(OtherRateType.PickRateMax)?.rate,
      binsEntered,
      pickExists: !!pickRecord,
    },
  });
  const {
    clearErrors,
    control,
    formState: { errors, isDirty, isSubmitting },
    getValues,
    setValue,
    setError,
    handleSubmit,
    register,
    reset,
  } = methods;
  const {
    fields: harvestDataRecordFields,
    append: appendHarvestDataRecord,
    remove: removeHarvestDataRecord,
  } = useFieldArray({
    control,
    name: 'harvestDataRecords',
  });
  const {
    fields: haulDataRecordFields,
    append: appendHaulDataRecord,
    remove: removeHaulDataRecord,
  } = useFieldArray({
    control,
    name: 'haulDataRecords',
  });

  // Clean up if user removes bins after entering other fields on partial entry.
  useEffect(() => {
    if (!binsEntered) {
      const ctrRecords = getValues('harvestDataRecords');

      setValue(
        'harvestDataRecords',
        ctrRecords.map(record => ({
          ...record,
          binsHauled: null,
          tarps: null,
          haulRate: null,
        })),
      );
      setValue('haulDataRecords', []);
      setValue('binsLeft', null);
      setValue('fbPerTree', null);
      setValue('notes', null);
    }
  }, [binsEntered, getValues, setValue]);

  const mapParams = validateCoordinateInfo(
    block.latitude,
    block.longitude,
    block.blockId,
  );

  const submitPartialPickRecord = async (
    harvestDataRecords: Omit<HarvestData, 'id'>[],
  ) => {
    const data = harvestDataRecords.map(record => ({
      scheduledPickId: parseInt(id, 10),
      contractorId: record.contractor?.value.id,
      startTime: record.startTime,
      headCount: record.headCount && parseInt(record.headCount, 10),
      hoursWorked: formatFromTimeDuration(record.timeWorked),
      transferTime: formatFromTimeDuration(record.transferTime),
      pickRate: record.pickRate && parseFloat(record.pickRate),
    })) as PartialPickPayload[];

    const isSuccessful = await savePartialPickData(data);

    if (isSuccessful) {
      reset();
      history.push(`${Constants.routes.PICK_SCHEDULE}/${id}`);
    }
  };

  const submitPickRecord = async ({
    harvestDataRecords,
    haulDataRecords,
    binsLeft,
    fbPerTree,
    notes,
  }: PickDataValues) => {
    let totalBinsPicked = 0;
    let totalBinsHauled = 0;

    const data = {
      schedulePickId: parseInt(id, 10),
      ...(pickRecord && {
        pickRecordId: pickRecord.id,
      }),
      contractorRecords: harvestDataRecords.map(record => {
        if (record.binsPicked) {
          totalBinsPicked += parseFloat(record.binsPicked);
        }
        if (record.binsHauled) {
          totalBinsHauled += parseFloat(record.binsHauled);
        }

        return {
          ...(record.id && {
            id: parseInt(record.id, 10),
          }),
          startTime: record.startTime,
          contractor: record.contractor?.value,
          headCount: record.headCount && parseInt(record.headCount, 10),
          hoursWorked: formatFromTimeDuration(record.timeWorked),
          transferTime: formatFromTimeDuration(record.transferTime),
          binsPicked: record.binsPicked && parseFloat(record.binsPicked),
          binsHauled: record.binsHauled && parseFloat(record.binsHauled),
          pickRate: record.pickRate && parseFloat(record.pickRate),
          tarps: record.tarps && parseInt(record.tarps, 10),
          haulRate: record.haulRate && record.haulRate.value,
        };
      }),
      haulerRecords: haulDataRecords.map(record => {
        if (record.binsHauled) {
          totalBinsHauled += parseFloat(record.binsHauled);
        }

        return {
          ...(record.id && {
            id: parseInt(record.id, 10),
          }),
          binsHauled: record.binsHauled && parseFloat(record.binsHauled),
          tarps: record.tarps && parseInt(record.tarps, 10),
          haulRate: record.haulRate?.value,
          hauler: record.hauler?.value,
          surcharge: record.haulRate?.value.surcharge,
        };
      }),
      binsLeft: binsLeft && parseInt(binsLeft, 10),
      fbPerTree: fbPerTree && parseInt(fbPerTree, 10),
      notes,
      seasonId: pool.seasonId,
      blockId,
      growerId: block.grower.id,
    } as PickRecordPayload;

    if (roundBins(totalBinsPicked) !== roundBins(totalBinsHauled)) {
      // Set a generic error to avoid useForm's 'isSubmitSuccessful' to be true
      setError('root.binsDoNotMatch', { type: 'pattern' });
      notifier.showErrorMessage(
        'The number of bins picked must match the number of bins hauled.',
      );
      return;
    }

    const isSuccessful = await savePickData(data);

    if (isSuccessful) {
      reset();
      history.push(`${Constants.routes.PICK_SCHEDULE}/${id}`);
    }
  };

  const onFormSubmit = async (data: PickDataValues) => {
    if (isDirty) {
      if (data.harvestDataRecords.some(record => record.binsPicked)) {
        await submitPickRecord(data);
      } else {
        await submitPartialPickRecord(data.harvestDataRecords);
      }
    } else {
      history.push(`${Constants.routes.PICK_SCHEDULE}/${id}`);
    }
  };

  useEffect(() => {
    if (fetchCrewsError) {
      handleError(fetchCrewsError, 'Unable to load crews.');
    }
    if (fetchRatesError) {
      handleError(fetchRatesError, 'Unable to load haul rates.');
    }
    if (otherRatesError) {
      handleError(otherRatesError, 'Unable to load pick rate min/max.');
    }
  }, [fetchCrewsError, fetchRatesError, otherRatesError]);

  return (
    <FormProvider<PickDataValues> {...methods}>
      <PickDataWizardContext.Provider
        value={{
          contractors,
          haulers: contractors,
          haulRates,
          isLoading,
          binsEntered: totalBinsPicked === 0 && !pickRecord,
          setTotalBinsPicked,
        }}
      >
        <PickDataWizardContainer
          backgroundColor={orange}
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <WizardHeader
            growerId={block.grower?.growerId}
            blockId={block.blockId}
            evaluator={`${block.primaryEvaluator?.firstName} ${block.primaryEvaluator?.lastName}`}
            varietyCode={block.variety?.varietyCode}
            binsRemaining={block.seasonData?.harvestProgress?.internalBinsLeft}
            size={size?.value}
            mapParams={mapParams}
            locationStyles={{ backgroundColor: darkNavy, iconColor: white }}
            textColor={darkNavy}
            showSize
          />
          <PickSummary
            packHouseCode={packHouse?.code}
            size={size?.value}
            market={market || undefined}
            pickDay={formatPickDetailDate(schedule.pickDay)}
            binsToPick={bins.toString()}
            pool={pool?.poolId}
            cleanPick={cleanPick ? 'Yes' : 'No'}
            pickType={
              typeof pickType === 'number'
                ? pickTypeLabels[pickType]
                : undefined
            }
            notes={notes}
            textColor={darkNavy}
          />
          <NavyPrompt>Assigned Staff/Crew</NavyPrompt>
          <MobileWrapper>
            <SummaryContainer>
              <WizardRow>
                <AssignmentLabel>HC</AssignmentLabel>
                <DataInfoValue $color={darkNavy}>
                  {`${coordinator?.firstName} ${coordinator?.lastName}` || '-'}
                </DataInfoValue>
              </WizardRow>
              {pickAssignment?.contractorPickAssignment?.map(
                (assignment, index) => (
                  <WizardRow key={assignment.id}>
                    <AssignmentLabel>FLC {index + 1}</AssignmentLabel>
                    <DataInfoValue $color={darkNavy}>
                      {`${assignment.contractor.code} (${assignment.bins} bins)`}
                    </DataInfoValue>
                  </WizardRow>
                ),
              )}
              {pickAssignment?.haulers?.map((assignment, index) => (
                <WizardRow key={assignment.id}>
                  <AssignmentLabel>Hauler {index + 1}</AssignmentLabel>
                  <DataInfoValue $color={darkNavy}>
                    {assignment.code}
                  </DataInfoValue>
                </WizardRow>
              ))}
            </SummaryContainer>
            <SummaryContainer>
              {pickAssignment?.notes && (
                <>
                  <InfoLabel>Harvest Notes:</InfoLabel>
                  <NavyNotes>{pickAssignment?.notes}</NavyNotes>
                </>
              )}
            </SummaryContainer>
          </MobileWrapper>
          <WizardDivider />
          <NavyPrompt>Add Harvest Data</NavyPrompt>
          <ItemContainer<HarvestData>
            columnNumber={2}
            itemName='FLC'
            items={harvestDataRecordFields}
            addItem={() => {
              if (!getValues('harvestDataRecords').length)
                clearErrors('harvestDataRecords');
              return appendHarvestDataRecord({
                id: '',
                startTime: null,
                contractor: null,
                headCount: null,
                timeWorked: { hours: 0, minutes: 0 },
                transferTime: { hours: 0, minutes: 0 },
                binsPicked: null,
                binsHauled: null,
                pickRate: defaultPickRate?.rate.toString() || null,
                tarps: null,
                haulRate: null,
              });
            }}
            removeItem={(index: number) => removeHarvestDataRecord(index)}
            error={
              (errors?.harvestDataRecords as unknown as FieldError)?.message
            }
            ItemComponent={HarvestDataForm}
            iconColor={darkNavy}
            textColor={white}
          />
          <WizardDivider />
          <NavyPrompt>Add Haul Data</NavyPrompt>
          <ItemContainer<HaulData>
            columnNumber={2}
            itemName='Hauler'
            items={haulDataRecordFields}
            addItem={() => {
              if (!getValues('haulDataRecords').length)
                clearErrors('haulDataRecords');
              return appendHaulDataRecord({
                id: '',
                hauler: null,
                binsHauled: null,
                tarps: null,
                haulRate: null,
              });
            }}
            removeItem={(index: number) => removeHaulDataRecord(index)}
            error={(errors?.haulDataRecords as unknown as FieldError)?.message}
            ItemComponent={HaulDataForm}
            isLoading={isLoadingRates}
            iconColor={darkNavy}
            textColor={white}
          />
          <WizardDivider />
          <MobileWrapper>
            <SummaryContainer>
              <NavyPrompt>Update Estimates</NavyPrompt>
              <DataEntryRow>
                <InfoLabel>Bins Left</InfoLabel>
                <WizardNumericInput name='binsLeft' isDisabled={!binsEntered} />
              </DataEntryRow>
              <DataEntryRow>
                <InfoLabel>FB/Tree</InfoLabel>
                <WizardNumericInput
                  name='fbPerTree'
                  isDisabled={!binsEntered}
                />
              </DataEntryRow>
            </SummaryContainer>
            <SummaryContainer>
              <TotalBinsContainer>
                <DataInfoValue $color={darkNavy}>
                  Total Bins Picked: {totalBinsPicked}
                </DataInfoValue>
              </TotalBinsContainer>
            </SummaryContainer>
          </MobileWrapper>
          <InfoLabel style={{ marginTop: '20px' }}>Notes</InfoLabel>
          <Textarea {...register('notes')} rows={2} isDisabled={!binsEntered} />
          <ButtonSection>
            <CancelBtn
              type='button'
              onClick={() =>
                history.push(`${Constants.routes.PICK_SCHEDULE}/${id}`)
              }
              disabled={isSubmitting}
            >
              Cancel
            </CancelBtn>
            <NavyButton type='submit' disabled={isSubmitting}>
              CONFIRM
            </NavyButton>
          </ButtonSection>
        </PickDataWizardContainer>
      </PickDataWizardContext.Provider>
    </FormProvider>
  );
};
